import classnames from 'classnames';
import { map, get } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { Container } from 'components/Grid';
import ScrollTo from 'components/ScrollTo';
import { small } from 'constants/mediaQueries';
import Theme from 'styles/theme';

class Tabs extends PureComponent {
  constructor(props) {
    super(props);

    this.containerRef = React.createRef();
    this.tabRefs = map(props.tabs, () => React.createRef());
    this.state = { tabIndicatorOffsetLeft: 0, tabIndicatorWidth: 0 };
  }

  componentDidMount() {
    this.updateTabs();
  }

  componentDidUpdate(prevProps) {
    if (this.props.activeTabIndex !== prevProps.activeTabIndex) {
      this.updateTabs();
    }
  }

  updateTabs() {
    this.scrollToActiveTab();
    this.updateActiveTabIndicator();
  }

  scrollToActiveTab() {
    const { activeTabIndex } = this.props;
    const activeChild = this.tabRefs[activeTabIndex];
    const containerElement = this.containerRef.current;

    const { offsetLeft, offsetWidth } = activeChild.current;
    const tabCenterPoint = offsetLeft + offsetWidth / 2;

    containerElement.scrollLeft = tabCenterPoint - containerElement.offsetWidth / 2;
  }

  updateActiveTabIndicator() {
    const { activeTabIndex } = this.props;
    const activeTabRef = this.tabRefs[activeTabIndex];
    const tabIndicatorOffsetLeft = get(activeTabRef, 'current.offsetLeft', 0);
    const tabIndicatorWidth = get(activeTabRef, 'current.clientWidth', 0);

    this.setState({ tabIndicatorOffsetLeft, tabIndicatorWidth });
  }

  render() {
    const {
      tabs,
      activeTabIndex,
      onTabClick,
      linkUrls,
      enableScrollTo,
      isButton,
      fullWidthUnderLine,
    } = this.props;
    const { tabIndicatorOffsetLeft, tabIndicatorWidth } = this.state;

    return (
      <div
        className={classnames('Tabs', {
          'Tabs--full-width-underline': fullWidthUnderLine,
        })}
        ref={this.containerRef}
      >
        <Container>
          <div className="Tabs__wrapper">
            {map(tabs, (label, index) => {
              const buttonClasses = classnames('Tabs__tab-button', {
                'Tabs__tab-button--active': index === activeTabIndex,
              });
              return (
                <div className="Tabs__tab" key={`tab-${index}`} ref={this.tabRefs[index]}>
                  {isButton ? (
                    <button
                      className={buttonClasses}
                      onClick={() => {
                        onTabClick(index);
                      }}
                    >
                      {label}
                    </button>
                  ) : enableScrollTo ? (
                    <ScrollTo
                      className={buttonClasses}
                      target={`row-${index}`}
                      onClick={() => {
                        onTabClick(index);
                      }}
                    >
                      {label}
                    </ScrollTo>
                  ) : (
                    <a
                      className={buttonClasses}
                      href={get(linkUrls, index)}
                      onClick={() => {
                        onTabClick(get(linkUrls, index));
                      }}
                    >
                      {label}
                    </a>
                  )}
                </div>
              );
            })}
            <div
              className="Tabs__active-tab-indicator"
              style={{ left: tabIndicatorOffsetLeft, width: tabIndicatorWidth }}
            />
          </div>
        </Container>

        <style jsx>
          {`
            @media ${small} {
              .Tabs {
                overflow-x: scroll;
                scroll-behavior: smooth;
                -ms-overflow-style: none; /* IE and Edge */
                scrollbar-width: none; /* Firefox */
              }

              /* Hide scrollbar for Chrome, Safari and Opera */
              .Tabs::-webkit-scrollbar {
                display: none;
              }
            }

            .Tabs__wrapper {
              position: relative;
              display: flex;
              height: 52px;
            }

            .Tabs__tab {
              width: 100%;
              text-align: center;
              margin: 0 ${Theme.get('spacing') * 3}px;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .Tabs--full-width-underline .Tabs__tab {
              margin: 0;
            }

            @media ${small} {
              .Tabs__tab {
                white-space: nowrap;
              }

              .Tabs__tab:nth-last-child(2) {
                border-right: ${Theme.get('spacing') * 3}px solid transparent;
              }

              .Tabs--full-width-underline .Tabs__tab:nth-last-child(2) {
                border-right: 0;
              }
            }

            .Tabs__active-tab-indicator {
              position: absolute;
              left: 0;
              bottom: -2px;
              height: 2px;
              background-color: ${Theme.get('colors-black')};
              transition: left ${Theme.get('transition-duration-ms')}
                ${Theme.get('transition-timing-ease-in-out')};
            }

            @media ${small} {
              .Tabs__active-tab-indicator {
                bottom: 0px;
                transition: none;
              }
            }
          `}
        </style>

        <style jsx global>
          {`
            .Tabs__tab-button {
              display: block;
              appearance: none;
              padding: 0 ${Theme.get('spacing') * 3}px;
              margin-top: ${Theme.get('spacing') / 2}px;
              color: ${Theme.get('colors-black-80')};
              font-size: ${Theme.get('font-size-px')};
              font-weight: ${Theme.get('font-weight-bold')};
              text-transform: uppercase;
              text-decoration: none;
              letter-spacing: 2px;
              background-color: transparent;
              cursor: pointer;
              border: 0;
              transition: color ${Theme.get('transition-duration-fast-ms')}
                ${Theme.get('transition-timing-ease-in-out')};
            }

            .Tabs__tab-button:hover,
            .Tabs__tab-button--active {
              color: ${Theme.get('colors-black')};
            }

            .Tabs--full-width-underline .Container {
              padding: 0;
            }

            @media ${small} {
              .Tabs__tab-button {
                transition: none;
              }
            }

            @media ${small} {
              .Tabs .Container {
                padding: 0;
              }
            }
          `}
        </style>
      </div>
    );
  }
}

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeTabIndex: PropTypes.number.isRequired,
  onTabClick: PropTypes.func,
  enableScrollTo: PropTypes.bool,
  fullWidthUnderLine: PropTypes.bool,
  linkUrls: PropTypes.arrayOf(PropTypes.string),
  isButton: PropTypes.bool,
};

Tabs.defaultProps = {
  onTabClick: () => {},
  enableScrollTo: true,
  fullWidthUnderLine: false,
  isButton: false,
  linkUrls: [],
};

export default Tabs;
